import { Skeleton, Table } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { RecycleSvg } from '../../../assets/icons/RecycleSvg';
import { useMetrics } from '../../../hooks/useMetrics';
import usePresentationVersion from '../../../hooks/usePresentationVersion';
import { Presentation } from '../../../services/backendService/types';
import { Button } from '../../_atoms/Button';
import BaseModal from '../../_molecules/BaseModal';
import SlideWrapperViwer from '../../SlideComponents/SlideWrapperViewer';

const VersionControl = ({
  presentationId,
  handleClose,
}: {
  presentationId: string;
  handleClose: () => void;
}) => {
  const [versionClicked, setVersionClicked] = useState<Presentation | undefined>();
  const { ref: scrollRef, inView } = useInView();
  const metrics = useMetrics();
  const {
    presentationVersions,
    restorePresentationVersion,
    isRestoring,
    isFetching,
    canFetchMore,
    fetchNextPage,
    isFetchingMore,
  } = usePresentationVersion({
    presentationId,
  });

  useEffect(() => {
    if (inView && canFetchMore && !isFetchingMore && !isFetching) {
      fetchNextPage();
    }
  }, [inView, canFetchMore, fetchNextPage, isFetchingMore, isFetching]);
  const dataSource = useMemo(() => {
    return (
      presentationVersions?.map(version => {
        return {
          key: version.id,
          createdAt: dayjs(version.createdAt).format('D MMM HH:mm - YYYY '),
          slidesNumber:
            version.content.slides.filter(s => !s.dataReferenceSlideId).length || 0,
          version: version,
        };
      }) || []
    );
  }, [presentationVersions]);

  const columns = [
    {
      title: 'Changed',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Number of slides',
      dataIndex: 'slidesNumber',
      key: 'slidesNumber',
    },
  ];

  return (
    <BaseModal visible={true} onCancel={() => handleClose()}>
      <h1>Presentation history</h1>

      {versionClicked && (
        <RestoreContainer>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3fr 1fr',
              alignItems: 'end',
              padding: '0 10px',
            }}
          >
            <h1 style={{ margin: 0, textAlign: 'center' }}>Restore to this version?</h1>
            <Button
              styles={{ marginTop: '10px', padding: '5px' }}
              loading={isRestoring}
              type={'ghost'}
              icon={<RecycleSvg />}
              onClick={() => {
                metrics.logEvent('Creator.RestoreVersion');
                restorePresentationVersion({
                  presentationId,
                  versionId: versionClicked.id,
                  onSuccessFunction: () => {
                    window.location.reload();
                  },
                });
              }}
            >
              {' '}
              Restore
            </Button>
          </div>

          <SlideContainer>
            <div
              style={{
                width: 450,
                display: 'grid',
                gridAutoFlow: 'column',
                overflowX: 'auto',
                gap: '5px',
              }}
            >
              {versionClicked.content.slides
                .filter(s => !s.dataReferenceSlideId)
                .map(slide => (
                  <SelectedVersionSlideWrapperViewer key={slide.id}>
                    <SlideWrapperViwer
                      aspect={{ height: 1080, width: 1920 }}
                      slide={slide}
                      backgroundMedia={slide.backgroundMedia}
                      resultView={false}
                      showStaticPoll={true}
                      hideLogo={false}
                      isDiscoverTemplate={false}
                      isOwner={true}
                    />
                  </SelectedVersionSlideWrapperViewer>
                ))}
            </div>
          </SlideContainer>
        </RestoreContainer>
      )}
      {presentationVersions && (
        <PresentationTableContainer>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            onRow={({ version }) => {
              return {
                onClick: () => {
                  setVersionClicked(version);
                }, // click row
              };
            }}
          />
          {(isFetchingMore || isFetching) && (
            <TableSkeleton active={true} style={{ width: '100%' }} />
          )}
          <div ref={scrollRef} style={{ padding: '5px' }}></div>
        </PresentationTableContainer>
      )}
    </BaseModal>
  );
};

const PresentationTableContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
const RestoreContainer = styled.div`
  background-color: white;
  padding: 10px;
  margin: 10px 0px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  grid-template-columns: 2fr 1fr;
  align-items: center;
`;

const SelectedVersionSlideWrapperViewer = styled.div`
  border-radius: 10px;
  width: 300px;
  height: 180px;
  cursor: pointer;
  overflow: hidden;
  margin: 10px;
`;
const SlideContainer = styled.div`
  grid-column: 1 / 3;
  display: flex;
  gap: 5px;
  overflow-x: auto;
  width: 3000px;
`;
const TableSkeleton = styled(Skeleton.Button)`
  width: 100% !important;
`;
export default VersionControl;
